import { api } from "@/lib/backend";
import type { Me } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";

/**
 * Profile completeness + nudge shape returned by `GET /me` (#383). Not yet in the generated client
 * (we don't run `generate:api` here), so we model it locally and read it via a typed accessor.
 */
export interface ProfileNudge {
  shouldShow: boolean;
  intervalDays: number;
  lastShownAt: string | null;
}

export interface ProfileCompleteness {
  isComplete: boolean;
  missingFields: string[];
  nudge: ProfileNudge;
}

type MeWithCompleteness = Me & {
  profileCompleteness?: ProfileCompleteness | null;
};

/** Read the (un-generated) profileCompleteness block off a `/me` response. */
export function getProfileCompleteness(me: Me | null | undefined): ProfileCompleteness | null {
  if (!me) {
    return null;
  }
  return (me as MeWithCompleteness).profileCompleteness ?? null;
}

/**
 * Whether the profile-completion nudge should be presented for this user. True only when the
 * backend says the profile is incomplete AND the interval has elapsed — complete profiles (and
 * non-employee users with a null block) never trigger the nudge.
 */
export function shouldPresentProfileNudge(me: Me | null | undefined): boolean {
  const completeness = getProfileCompleteness(me);
  if (!completeness) {
    return false;
  }
  return !completeness.isComplete && completeness.nudge.shouldShow;
}

/** The missing-field keys to surface in the nudge (empty when complete/unknown). */
export function getMissingProfileFields(me: Me | null | undefined): string[] {
  return getProfileCompleteness(me)?.missingFields ?? [];
}

export interface UpdateDayOfBirthInput {
  /** ISO date-time string. */
  dayOfBirth: string;
  /** Explicit verification flag — must be true (geboortedatum ⇒ loonschalen). */
  confirmed: boolean;
}

/**
 * Self-service day-of-birth edit via the verified endpoint. Uses `api.request` directly because the
 * endpoint isn't in the generated client yet.
 */
export function updateDayOfBirth(input: UpdateDayOfBirthInput) {
  return extractionWrapper(
    api.request<{ dayOfBirth: string; dayOfBirthVerifiedAt: string }, unknown>({
      path: `/me/day-of-birth`,
      method: "PATCH",
      body: input,
      type: "application/json" as never,
      format: "json",
      secure: true,
    }),
  );
}

/** Tell the backend the nudge was shown so it respects the interval. */
export function markProfileNudgeShown() {
  return extractionWrapper(
    api.request<void, unknown>({
      path: `/me/profile-nudge/shown`,
      method: "POST",
      secure: true,
    }),
  );
}
